"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _toMilliseconds = _interopRequireDefault(require("./toMilliseconds"));
var _getDiffBetweenDates = _interopRequireDefault(require("./getDiffBetweenDates"));
var _dateFormat = _interopRequireDefault(require("./dateFormat"));
var _getMinute = _interopRequireDefault(require("./getMinute"));
var _getHour = _interopRequireDefault(require("./getHour"));
var _getDay = _interopRequireDefault(require("./getDay"));
var _getWeek = _interopRequireDefault(require("./getWeek"));
var _getMonth = _interopRequireDefault(require("./getMonth"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * `chronos` is a versatile date utility that wraps around `dateFormat`.
 * It provides both a default formatted date string and methods for extracting specific date and time components.
 *
 * @param {Time} [date=new Date()] - The date to format. Defaults to the current date.
 * @returns {string | Object} - The formatted date string or an object with methods for specific date components.
 *
 * @example
 * const chrono = chronos(new Date())
 *
 * // Default behavior (formatted date string)
 * console.log(chrono) // "25 de diciembre de 2024 11:59 PM"
 *
 * // Use specific date/time component methods
 * console.log(chrono.year()) // 2024
 * console.log(chrono.month()) // 12
 *
 * // Use in template literals or concatenation
 * console.log(`Date: ${chrono}`) // "Date: 25 de diciembre de 2024 11:59 PM"
 */
function chronos() {
  let date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Date();
  const localeDate = new Date(date);
  const timezoneOffset = localeDate.getTimezoneOffset() * 60000;
  const standardizedDate = new Date(localeDate.getTime() + timezoneOffset);

  // Instance methods to access specific date components
  const instance = {
    /**
     * Format the date based on provided options
     * @param {Partial<DateFormatOptions>} options - Formatting options
     * @returns {string} - The formatted date string
     */
    format: options => (0, _dateFormat.default)(localeDate, options),
    /**
     * Retrieves the year component of the date.
     * @returns {number} - The year (e.g., 2024).
     */
    year: () => standardizedDate.getFullYear(),
    /**
     * Retrieves the month component of the date (1-based, e.g., January = 1).
     * @returns {number} - The month (e.g., 12 for December).
     */
    month: function () {
      let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return (0, _getMonth.default)(standardizedDate, options);
    },
    /**
     * Retrieves the week day based on the specified options.
     * @param {WeekOptions} [options={}] - Options for configuring the week format.
     * @returns {string} - The formatted week string (e.g., "Mon", "Monday").
     */
    week: function () {
      let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return (0, _getWeek.default)(standardizedDate, options);
    },
    /**
     * Retrieves the formatted day component of the time based on the specified options.
     * @param {DayOptions} [options={}] - Options for configuring the day format.
     * @returns {string} - The formatted day string (e.g., "12 Sept", "13 Tue").
     */
    day: function () {
      let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return (0, _getDay.default)(standardizedDate, options);
    },
    /**
     * Retrieves the hours component of the time based on the specified options.
     * @param {HourOptions} [options={}] - Options for configuring the hour format.
     * @returns {string | number} - The hours (e.g., 23 for 11 PM in 24-hour format, or "11 PM" in 12-hour format).
     */
    hour: function () {
      let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return (0, _getHour.default)(standardizedDate, options);
    },
    /**
     * Retrieves the minutes component of the time based on the specified options.
     * @param {MinuteOptions} [options={}] - Options for configuring the minute format.
     * @returns {string | number} - The minutes (e.g., "01", "02" for padded, or 1, 2 for unpadded).
     */
    minute: function () {
      let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return (0, _getMinute.default)(standardizedDate, options);
    },
    /**
     * Retrieves date and time components as an object with `date` and `time` properties.
     * @returns {Object} - An object containing `date` and `time` strings.
     * @example
     * { date: "25 de diciembre de 2024", time: "11:59 PM" }
     */
    getTime: options => {
      const timeOptions = _objectSpread({
        withHours: true
      }, options || {});
      const formattedTime = (0, _dateFormat.default)(date, timeOptions);
      const [dateTime, hoursTime] = formattedTime.split(/(\d{1,2}:\d{2} [APM]{2})/).filter(Boolean) || [];
      return {
        dateTime: dateTime === null || dateTime === void 0 ? void 0 : dateTime.trim(),
        hoursTime: hoursTime === null || hoursTime === void 0 ? void 0 : hoursTime.trim()
      };
    },
    /**
     * Subtracts a given amount of time from the current date.
     * @param {number} value - The amount of time to subtract.
     * @param {TimeType} type - The unit of the provided time.
     * @returns {Date} - The resulting date.
     * @example
     * const updatedDate = chronos(new Date()).subtract(5, 'days')
     */
    subtract: (value, type) => {
      const timeToSSubtract = (0, _toMilliseconds.default)(value, type);
      return new Date(date.getTime() - timeToSSubtract);
    },
    /**
     * Converts a given time value to milliseconds.
     * @function
     * @name toMilliseconds
     * @param {number} value - The numerical value to convert.
     * @param {TimeType} type - The unit of time (e.g., seconds, minutes, hours).
     * @returns {number} The equivalent time in milliseconds.
     */
    toMilliseconds: (value, type) => {
      return (0, _toMilliseconds.default)(value, type);
    },
    /**
     * Returns the difference in granularity (e.g., seconds, minutes, hours, days) between the current date and another date.
     * @param {Time} end - The end date to compare with.
     * @param {TimeType} granularity - The unit of time for the difference ('seconds', 'minutes', 'hours', 'days', 'months', 'years').
     * @returns {number} - The difference between the two dates in the specified granularity.
     * @example
     * const diffInDays = chronos().diff('2025-01-01', 'days')
     */
    diff: (end, granularity) => {
      return (0, _getDiffBetweenDates.default)(date, end, granularity);
    },
    /**
     * Converts the current date into an ISO 8601 string while accounting for the timezone offset.
     * @returns {string} - The ISO 8601 formatted string of the date (e.g., "2025-01-23T12:34:56.789Z").
     * @example
     * const chrono = chronos(new Date("2025-01-23T09:34:56.789-03:00"))
     * console.log(chrono.toISOString())
     * // Output: "2025-01-23T12:34:56.789Z"
     */
    toISOString: () => {
      const time = date;
      const timezoneOffset = time.getTimezoneOffset() * 60000;
      const localDate = new Date(time.getTime() - timezoneOffset);
      return localDate.toISOString();
    }
  };
  return instance;
}
var _default = exports.default = chronos;